import { FC, RefObject } from 'react';

import {
    ACCOUNT_CONTROL_PAGE_ROUTE,
    ACCOUNT_CREATE_PAGE_ROUTE,
    SUBSCRIPTIONS_PAGE_ROUTE,
    VACANCIES_LINK,
} from 'utils/constants/app';

import profileBlockButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/header/profile/profile_block_button_click';
import { ActivatorElementType } from '@hh.ru/magritte-internal-drop-base';
import { BottomSheet, Button, Divider, Drop, Text, VSpacing } from '@hh.ru/magritte-ui';
import {
    ArrowRightOutlinedSize16,
    CrossOutlinedSize24,
    DocumentOutlinedSize24,
    EnvelopeOpenedOutlinedSize24,
    PenOutlinedSize24,
    PlusOutlinedSize24,
} from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { AccountSelect } from 'components/Header/HeaderUserDrop/AccountSwitcher/AccountSwitcher';
import { EmployerInfoChip } from 'components/Header/HeaderUserDrop/EmployerInfoChip/EmployerInfoChip';
import { EndSessionButton } from 'components/Header/HeaderUserDrop/EndSessionButton/EndSessionButton';
import { LogoutButton } from 'components/Header/HeaderUserDrop/LogoutButton/LogoutButton';
import { UserDropButton } from 'components/Header/HeaderUserDrop/UserDropButton/UserDropButton';

import { setRequisitesFormOpen } from 'store/models/requisites';
import { SessionType } from 'store/models/user';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/Header/HeaderUserDrop/HeaderUserDrop.module.less';

interface HeaderUserDropProps {
    dropActivatorRef: RefObject<ActivatorElementType>;
    isDropVisible: boolean;
    setIsDropVisibleOff: () => void;
}

const TrlKeys = {
    requisites: 'Header.HeaderUserDrop.requisites',
    subscriptions: 'Header.HeaderUserDrop.subscriptions',
    accountCreate: 'Header.HeaderUserDrop.accountCreate',
    accountControl: 'Header.HeaderUserDrop.accountControl',
    goToHH: 'Header.HeaderUserDrop.goToHH',
};

export const HeaderUserDrop: FC<HeaderUserDropProps> = ({ dropActivatorRef, isDropVisible, setIsDropVisibleOff }) => {
    const isSudo = useSelector((state) => state.user.sessionType === SessionType.Sudo);
    const employerId = useSelector(({ user }) => user.user?.employerId);
    const userName = useSelector(({ user }) => user.user?.name);

    const dispatch = useDispatch();
    const trls = useTrls();

    const content = (
        <>
            <div className={styles.dropTopWrapper}>
                <div className={styles.dropClose}>
                    <CrossOutlinedSize24
                        padding={8}
                        initialColor="secondary"
                        highlightedColor="primary"
                        onClick={setIsDropVisibleOff}
                        data-qa="drop-close"
                    />
                </div>
                {userName && (
                    <Text typography="title-5-semibold">
                        <div className={styles.dropNameWrapper} data-qa="user-drop-name">
                            {userName}
                        </div>
                    </Text>
                )}
                {employerId && <EmployerInfoChip />}
                <AccountSelect />
                <EndSessionButton />
            </div>
            {!isSudo && (
                <>
                    <VSpacing default={24} />
                    <Divider />
                </>
            )}
            <div className={styles.dropNavButtonsWrapper}>
                <UserDropButton
                    dataQa="requisites-form-open-button"
                    text={trls(TrlKeys.requisites)}
                    Icon={DocumentOutlinedSize24}
                    onClick={() => {
                        dispatch(setRequisitesFormOpen(true));
                        setIsDropVisibleOff();
                    }}
                />
                <UserDropButton
                    dataQa="subscriptions-page-link"
                    text={trls(TrlKeys.subscriptions)}
                    Icon={EnvelopeOpenedOutlinedSize24}
                    to={SUBSCRIPTIONS_PAGE_ROUTE}
                    onClick={() => profileBlockButtonClick({ blockName: 'email_settings' })}
                />
                {isSudo && (
                    <>
                        <UserDropButton
                            dataQa="account-create-link"
                            text={trls(TrlKeys.accountCreate)}
                            Icon={PlusOutlinedSize24}
                            to={ACCOUNT_CREATE_PAGE_ROUTE}
                            onClick={() => profileBlockButtonClick({ blockName: 'create_account' })}
                        />
                        <UserDropButton
                            dataQa="account-control-link"
                            text={trls(TrlKeys.accountControl)}
                            Icon={PenOutlinedSize24}
                            to={ACCOUNT_CONTROL_PAGE_ROUTE}
                            onClick={() => profileBlockButtonClick({ blockName: 'accounts_control' })}
                        />
                    </>
                )}
            </div>
            <div className={styles.dropBottomButtonsWrapper}>
                <Button
                    Element="a"
                    href={VACANCIES_LINK}
                    mode="secondary"
                    style="accent"
                    size="small"
                    stretched
                    icon={<ArrowRightOutlinedSize16 />}
                    spaceBetween
                    data-qa="go-to-hh-link"
                >
                    {trls(TrlKeys.goToHH)}
                </Button>
                <LogoutButton />
            </div>
        </>
    );

    return (
        <>
            <Drop
                style={{ marginTop: 24 }}
                width={480}
                visible={isDropVisible}
                activatorRef={dropActivatorRef}
                placement="bottom-right"
                forcePlacement
                onClose={setIsDropVisibleOff}
                padding={0}
                autoFocusWhenOpened={false}
            >
                {content}
            </Drop>
            <BottomSheet
                height="content"
                visible={isDropVisible}
                onClose={setIsDropVisibleOff}
                data-qa="header-user-drop"
                footer={content}
            />
        </>
    );
};
